import { useEffect, useRef, useState, type ComponentType } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "motion/react";

import StacyBurger from "./StacyBurger";
import StaycheeseBurger from "./StaycheeseBurger";
import ClassicBurger from "./ClassicBurger";
import BroadwayBurger from "./BroadwayBurger";
import ChapoBurger from "./ChapoBurger";
import StacykidsBurger from "./StacykidsBurger";
import ManhattanBurger from "./ManhattanBurger";
import DeLaHuertaBurger from "./DeLaHuertaBurger";

const DESKTOP_BREAKPOINT = 1280;
const COMPACT_BURGER_STAGE_WIDTH = 298;
const COMPACT_CARD_MIN_WIDTH = 300;

interface BurgerData {
  id: string;
  name: string;
  badge?: string;
  color: "red" | "blue";
  ingredients: {
    topBun: boolean;
    sauce1?: string;
    cheese?: boolean;
    meat: boolean;
    sauce2?: string;
    bottomBun: boolean;
    labels?: string[];
  };
}

const BURGERS: BurgerData[] = [
  {
    id: "stacy",
    name: "STACY",
    badge: "de la casa",
    color: "red",
    ingredients: {
      topBun: true,
      sauce1: "Thousand Island",
      cheese: true,
      meat: true,
      sauce2: "Thousand Island",
      bottomBun: true,
      labels: ["SALSA STACY", "CHEDDAR X2", "CARNE"],
    },
  },
  {
    id: "staycheese",
    name: "STAYCHEESE",
    color: "blue",
    ingredients: {
      topBun: true,
      cheese: true,
      meat: true,
      bottomBun: true,
      labels: ["CHEDDAR X2", "CARNE"],
    },
  },
  {
    id: "classic",
    name: "CLASSIC",
    color: "blue",
    ingredients: {
      topBun: true,
      meat: true,
      bottomBun: true,
      labels: ["CARNE"],
    },
  },
  {
    id: "broadway",
    name: "BROADWAY",
    badge: "Stacy hit / Burger nro 1",
    color: "blue",
    ingredients: {
      topBun: true,
      cheese: true,
      meat: true,
      bottomBun: true,
      labels: ["QUESO AZUL", "CARNE"],
    },
  },
  {
    id: "ladelchapo",
    name: "LA DEL CHAPO",
    badge: "mexicana",
    color: "blue",
    ingredients: {
      topBun: true,
      cheese: true,
      meat: true,
      bottomBun: true,
      labels: ["JALAPEÑOS", "CHEDDAR", "CARNE"],
    },
  },
  {
    id: "stacykids",
    name: "STACYKIDS",
    badge: "KIDS",
    color: "blue",
    ingredients: {
      topBun: true,
      cheese: true,
      meat: true,
      bottomBun: true,
      labels: ["CHEDDAR", "CARNE"],
    },
  },
  {
    id: "manhattan",
    name: "MANHATTAN",
    color: "blue",
    ingredients: {
      topBun: true,
      cheese: true,
      meat: true,
      bottomBun: true,
      labels: ["BACON", "CHEDDAR", "CARNE"],
    },
  },
  {
    id: "delahuerta",
    name: "DE LA HUERTA",
    badge: "veggie",
    color: "red",
    ingredients: {
      topBun: true,
      cheese: true,
      meat: true,
      bottomBun: true,
      labels: ["MEDALLÓN", "VEGETARIANO"],
    },
  },
];

const BURGER_COMPONENTS: Record<string, ComponentType<{ isExploded: boolean }>> = {
  stacy: StacyBurger,
  staycheese: StaycheeseBurger,
  classic: ClassicBurger,
  broadway: BroadwayBurger,
  ladelchapo: ChapoBurger,
  stacykids: StacykidsBurger,
  manhattan: ManhattanBurger,
  delahuerta: DeLaHuertaBurger,
};

function BurgerIcon({ color }: { color: "red" | "blue" }) {
  const strokeColor = color === "red" ? "var(--red-500)" : "var(--beige-500)";

  return (
    <div className="flex h-[60px] w-[80px] items-center justify-center">
      <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
        <path
          d="M10 15 Q10 5 20 5 L60 5 Q70 5 70 15"
          stroke={strokeColor}
          strokeWidth="3"
          fill="none"
        />
        <line x1="12" y1="25" x2="68" y2="25" stroke={strokeColor} strokeWidth="2" />
        <line x1="12" y1="32" x2="68" y2="32" stroke={strokeColor} strokeWidth="2" />
        <line x1="12" y1="39" x2="68" y2="39" stroke={strokeColor} strokeWidth="2" />
        <rect
          x="10"
          y="48"
          width="60"
          height="8"
          rx="4"
          stroke={strokeColor}
          strokeWidth="3"
          fill="none"
        />
      </svg>
    </div>
  );
}

function ExplodedIngredients({ burger }: { burger: BurgerData }) {
  const strokeColor = burger.color === "red" ? "var(--red-500)" : "var(--beige-500)";
  const textColor = burger.color === "red" ? "text-red-500" : "text-beige-500";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center gap-3 py-4"
    >
      {burger.ingredients.labels?.map((label, index) => (
        <motion.div
          key={label}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: -(index * 10), opacity: 1 }}
          exit={{ y: 0, opacity: 0 }}
          transition={{ delay: index * 0.05, type: "spring", stiffness: 300 }}
          className="flex items-center gap-2"
        >
          <span className={`font-['Diary_Notes',sans-serif] text-xs ${textColor}`}>
            {label}
          </span>
        </motion.div>
      ))}

      <motion.div
        initial={{ y: 0 }}
        animate={{ y: -80 }}
        exit={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="h-[30px] w-[120px]"
      >
        <svg width="120" height="30" viewBox="0 0 120 30" fill="none">
          <path
            d="M5 20 Q5 5 15 5 L105 5 Q115 5 115 20"
            stroke={strokeColor}
            strokeWidth="4"
            fill="none"
          />
        </svg>
      </motion.div>

      {burger.ingredients.labels?.map((_, index) => (
        <motion.div
          key={`layer-${index}`}
          initial={{ y: 0 }}
          animate={{ y: -(40 + index * 15) }}
          exit={{ y: 0 }}
          transition={{
            delay: index * 0.05,
            type: "spring",
            stiffness: 300,
            damping: 20,
          }}
          className="w-[120px]"
        >
          <svg width="120" height="15" viewBox="0 0 120 15">
            <rect
              x="5"
              y="5"
              width="110"
              height="8"
              rx="2"
              stroke={strokeColor}
              strokeWidth="3"
              fill="none"
            />
          </svg>
        </motion.div>
      ))}

      <motion.div
        initial={{ y: 0 }}
        animate={{ y: -20 }}
        exit={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="h-[20px] w-[120px]"
      >
        <svg width="120" height="20" viewBox="0 0 120 20" fill="none">
          <rect
            x="5"
            y="5"
            width="110"
            height="12"
            rx="4"
            stroke={strokeColor}
            strokeWidth="4"
            fill="none"
          />
        </svg>
      </motion.div>
    </motion.div>
  );
}

function BurgerVisual({
  burger,
  isActive,
  compact = false,
}: {
  burger: BurgerData;
  isActive: boolean;
  compact?: boolean;
}) {
  const BurgerComponent = BURGER_COMPONENTS[burger.id];

  if (BurgerComponent) {
    return (
      <div className="grid h-full w-full place-items-center overflow-visible">
        <div className="flex h-full w-full items-center justify-center overflow-visible">
          <div
            className="flex items-center justify-center overflow-visible"
            style={
              compact
                ? { width: `${COMPACT_BURGER_STAGE_WIDTH}px` }
                : { width: "100%" }
            }
          >
            <BurgerComponent isExploded={isActive} />
          </div>
        </div>
      </div>
    );
  }

  const textColor = burger.color === "red" ? "text-red-500" : "text-beige-500";

  return (
    <div
      className={[
        "grid w-full place-items-center overflow-hidden",
        compact
          ? "min-h-[300px] sm:min-h-[340px] md:min-h-[380px]"
          : "min-h-[360px] 2xl:min-h-[400px]",
      ].join(" ")}
    >
      <AnimatePresence mode="wait">
        {isActive ? (
          <ExplodedIngredients burger={burger} />
        ) : (
          <motion.div
            key="closed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-4 text-center"
          >
            <BurgerIcon color={burger.color} />
            <div>
              <h3
                className={`font-['Excessive_Extra_Condensed'] text-2xl tracking-wider sm:text-4xl ${textColor}`}
              >
                {burger.name}
              </h3>
              {burger.badge && (
                <p className={`mt-1 font-['Diary_Notes',sans-serif] text-xs sm:text-sm ${textColor}`}>
                  {burger.badge}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
function BurgerCard({
  burger,
  isActive,
  compact = false,
  onActiveChange,
}: {
  burger: BurgerData;
  isActive: boolean;
  compact?: boolean;
  onActiveChange?: (active: boolean) => void;
}) {
  const bgColor = burger.color === "red" ? "bg-beige-500" : "bg-blue-500";
  const borderColor = burger.color === "red" ? "border-red-500" : "border-beige-500";

  return (
    <motion.article
      onHoverStart={!compact ? () => onActiveChange?.(true) : undefined}
      onHoverEnd={!compact ? () => onActiveChange?.(false) : undefined}
      whileHover={!compact ? { y: -6, scale: 1.01 } : undefined}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className={`${bgColor} ${borderColor} h-full overflow-hidden rounded-2xl border-4`}
      style={{ boxShadow: "var(--shadow-600)" }}
    >
      <div className="grid h-full min-h-[400px] w-full place-items-center py-6 sm:py-8 md:py-10">
        <BurgerVisual burger={burger} isActive={isActive} compact={compact} />
      </div>
    </motion.article>
  );
}

function DesktopIntroCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.3 }}
      transition={{ duration: 0.8 }}
      className="flex min-h-[380px] flex-col justify-end"
    >
      <div>
        <h2 className="mb-4 font-['Excessive_Extra_Condensed'] text-[clamp(7rem,12vw,10.5rem)] leading-[0.9] tracking-wider text-beige-500">
          LAS BURGERS
        </h2>
        <p className="mb-4 font-['Diary_Notes',sans-serif] text-[32px] leading-[32px] tracking-[0.64px] text-beige-500">
          ELEGÍ TU FAVORITA
        </p>
      </div>

      <motion.a
        href="https://wa.me/5492613866837?text=%C2%A1Hola%20Stacy!%20Quiero%20hacer%20un%20pedido.%20%C2%BFMe%20comparten%20el%20men%C3%BA%3F"
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.03, boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)" }}
        whileTap={{ scale: 0.97 }}
        className="flex min-h-[64px] max-w-[580px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[240px]"
      >
        <p className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
          PEDILA AHORA
        </p>
      </motion.a>
    </motion.div>
  );
}

function DesktopGrid() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="grid grid-cols-3 items-stretch gap-8">
      <DesktopIntroCard />

      {BURGERS.map((burger) => (
        <BurgerCard
          key={burger.id}
          burger={burger}
          isActive={hoveredId === burger.id}
          onActiveChange={(active) => setHoveredId(active ? burger.id : null)}
        />
      ))}
    </div>
  );
}

function MobileHeader() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, amount: 0.3 }}
      transition={{ duration: 0.8 }}
      className="mb-8 px-6 pt-8"
    >
      <h2 className="mb-4 font-['Excessive_Extra_Condensed'] text-[88px] leading-[0.9] tracking-wider text-beige-500 sm:text-[110px] md:text-[140px] lg:text-[160px]">
        LAS BURGERS
      </h2>

      <p className="mb-6 font-['Diary_Notes',sans-serif] text-xl text-beige-500 sm:text-2xl md:text-3xl">
        ELEGÍ TU FAVORITA
      </p>

      <motion.a
        href="https://wa.me/5492613866837?text=%C2%A1Hola%20Stacy!%20Quiero%20hacer%20un%20pedido.%20%C2%BFMe%20comparten%20el%20men%C3%BA%3F"
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.03, boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)" }}
        whileTap={{ scale: 0.97 }}
        className="flex min-h-[64px] max-w-[580px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[240px]"
      >
        <p className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
          PEDILA AHORA
        </p>
      </motion.a>
    </motion.div>
  );
}

function MobileCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateSize = () => {
      setContainerWidth(node.offsetWidth);
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(node);

    window.addEventListener("resize", updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  const getCardRatio = () => {
    if (containerWidth >= 1024) return 0.48; // tablet horizontal / pantallas grandes compactas
    if (containerWidth >= 768) return 0.58;  // tablet
    if (containerWidth >= 640) return 0.72;  // mobile grande
    return 0.86; // mobile
  };

  const gap = containerWidth >= 640 ? 24 : 16;
  const cardWidth = Math.max(containerWidth * getCardRatio(), COMPACT_CARD_MIN_WIDTH);
  const sidePadding = Math.max((containerWidth - Math.min(cardWidth, containerWidth)) / 2, 16);

  const updateCurrentIndex = () => {
    const container = containerRef.current;
    if (!container) return;

    const containerCenter = container.scrollLeft + container.clientWidth / 2;

    let nextIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemRefs.current.forEach((item, index) => {
      if (!item) return;

      const itemCenter = item.offsetLeft + item.clientWidth / 2;
      const distance = Math.abs(itemCenter - containerCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        nextIndex = index;
      }
    });

    setCurrentIndex(nextIndex);
  };

  const goToSlide = (index: number) => {
    const container = containerRef.current;
    const item = itemRefs.current[index];
    if (!container || !item) return;

    const targetLeft =
      item.offsetLeft - container.clientWidth / 2 + item.clientWidth / 2;

    container.scrollTo({
      left: targetLeft,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    updateCurrentIndex();
  }, [containerWidth]);

  return (
    <div className="w-full pb-8">
      <div
        ref={containerRef}
        onScroll={updateCurrentIndex}
        className="no-scrollbar overflow-x-auto overflow-y-hidden"
        style={{
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          className="flex items-stretch"
        >
          <div
            aria-hidden="true"
            className="flex-none"
            style={{ width: `${sidePadding}px` }}
          />

          {BURGERS.map((burger, index) => {
            const isActive = index === currentIndex;

            return (
              <div
                key={burger.id}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                className="snap-center flex-none"
                style={{ width: `${cardWidth}px`, marginRight: index === BURGERS.length - 1 ? 0 : gap }}
              >
                <motion.div
                  animate={{
                    scale: isActive ? 1 : 0.92,
                    opacity: isActive ? 1 : 0.72,
                  }}
                  transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                  className="flex h-full w-full items-center justify-center"
                >
                  <div className="w-full">
                    <BurgerCard burger={burger} isActive={isActive} compact />
                  </div>
                </motion.div>
              </div>
            );
          })}

          <div
            aria-hidden="true"
            className="flex-none"
            style={{ width: `${sidePadding}px` }}
          />
        </div>
      </div>

      <div className="mt-8 flex justify-center gap-2">
        {BURGERS.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="group relative"
            aria-label={`Go to slide ${index + 1}`}
          >
            <motion.div
              className="h-3 w-3 rounded-full"
              animate={{
                backgroundColor:
                  index === currentIndex ? "var(--red-500)" : "var(--beige-500)",
                scale: index === currentIndex ? 1.2 : 1,
              }}
              whileHover={{ scale: 1.3 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
interface BurgersProps {
  className?: string;
}

export default function Burgers({ className = "" }: BurgersProps) {
  const [isCompactLayout, setIsCompactLayout] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });

  const opacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0.95, 1, 1, 0.98]);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactLayout(window.innerWidth < DESKTOP_BREAKPOINT);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section
      id="burgers"
      ref={sectionRef}
      className={`relative w-full bg-beige-500 px-4 py-20 sm:px-10 md:px-10 lg:px-10 ${className}`}
      data-name="Burgers Section"
    >
      <motion.div className="relative mx-auto max-w-[1920px]" style={{ opacity, scale }}>
        <div
          className="relative rounded-[24px] border border-blue-500 bg-blue-500 p-0 xl:p-8"
          style={{ boxShadow: "var(--shadow-700)" }}
        >
          {isCompactLayout ? <MobileHeader /> : null}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {isCompactLayout ? <MobileCarousel /> : <DesktopGrid />}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}